import React, { useState, useEffect } from 'react';
// Common Components
import AppPaginator from '../../AppPaginator';
import AppSearchBox from '../../AppSearchBox';
import AppBreadCrumb from '../../common/AppBreadCrumb';
import PerPageBlock from '../../common/PerPageBlock';
import AppToaster from '../../AppToaster';// API & Utils
import { getSalesReturns } from '../../../api/SalesReturnsAPI';
import { handleApiError } from '../../../api/risapi';
// Hooks & Helpers
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { format, parseISO } from 'date-fns';

const SalesReturnsPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const successMessage = location.state?.message;

    const [entityName, setEntityName] = useState('Sales Return');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewDataLoading, setViewDataLoading] = useState(false);
    const [searchTermCompleted, setSearchTermCompleted] = useState('');
    const [perPage, setPerPage] = useState(process.env.REACT_APP_PER_PAGE_COUNT || 10);

    const [returnsList, setReturnsList] = useState({
        paginate: true, data: [], per_page: perPage, current_page: 1, tot_page: null,
    });

    const [toaster, setToaster] = useState({});
    const [showToaster, setShowToaster] = useState(false);

    // --- Navigation ---
    const handleAddClick = () => navigate('/franchisee_user/sales/sales-returns/create');
    // "Edit" essentially acts as "View" for posted financial documents
    const handleEditClick = (id) => navigate(`/franchisee_user/sales/sales-returns/view/${id}`);

    // --- Delete Logic (Disabled by design to match Sales Invoice pattern) ---
    const handleDeleteClick = (id) => {
        Swal.fire(
            'Action Not Allowed',
            'Posted Sales Returns cannot be deleted. This ensures financial and stock integrity.',
            'warning'
          );
    };

    // --- Effects ---
    useEffect(() => {
         if (successMessage) {
            setShowToaster(true);
            setToaster({ type: 'success', heading: 'Success', message: successMessage });
            navigate(location.pathname, { replace: true, state: {} });
         }
     }, [successMessage, location.pathname, navigate]);

    useEffect(() => {
         document.title = 'Manage Sales Returns';
         let filters = { paginate: true, page: currentPage, per_page: perPage, search_term: searchTermCompleted };
         setViewDataLoading(true);

         getSalesReturns(filters, function (response) {
             setViewDataLoading(false);
             if (response.sales_returns) {
                 let data = []; let currentP = 1; let lastP = 1; let responsePP = perPage;
                 if (Array.isArray(response.sales_returns)) { data = response.sales_returns; }
                 else if (response.sales_returns.data && Array.isArray(response.sales_returns.data)) {
                     data = response.sales_returns.data; currentP = response.sales_returns.current_page; lastP = response.sales_returns.last_page; responsePP = response.sales_returns.per_page;
                 } else { console.error("API structure error:", response.sales_returns); handleApiError(response); return; }
                 setReturnsList({ paginate: filters.paginate, per_page: responsePP, data: data, current_page: currentP, tot_page: lastP });
             } else {
                  handleApiError(response); setToaster({ type: 'danger', heading: 'Error', message: 'Could not load Sales Returns.' }); setShowToaster(true);
                  setReturnsList(prev => ({ ...prev, data: [], current_page: 1, tot_page: 1 }));
             }
         });
     }, [currentPage, searchTermCompleted, perPage]);

    // Helper to format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try { return format(parseISO(dateString), 'dd-MMM-yyyy'); }
        catch (error) { return dateString; }
    };

    return (
        <>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <main>
                <div className="container-fluid">
                    <AppBreadCrumb title='Sales Returns List'>
                        <li><a className="f-s-14 f-w-500" href="#">Sales</a></li>
                        <li className="active"><a className="f-s-14 f-w-500" href="#">Sales Returns</a></li>
                    </AppBreadCrumb>

                    <div className="row bg-white">
                        <div className="col-12">
                            <div className="tab-wrapper mb-3">
                                <ul className="tabs">
                                    <li className="tab-link active" data-tab={1}>All Sales Returns</li>
                                    <li className="ms-md-auto">
                                        <div className="text-md-end d-flex flex-column flex-md-row mt-md-3">
                                            <AppSearchBox searchTerm={searchTerm} setSearchTerm={setSearchTerm} setSearchTermCompleted={setSearchTermCompleted} />
                                            <button className="text-nowrap btn btn-red btn-sm ms-md-3 fw-semibold f-s-14" type="button" onClick={handleAddClick}>
                                                <i className="ph-bold  ph-circles-three-plus"></i> Add Sales Return
                                            </button>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                            <div className="content-wrapper" id="card-container">
                                <div className="d-flex flex-wrap m-md-3">
                                    {returnsList.data.length > 0 ? (
                                        <>
                                            <AppPaginator entityName={entityName} currentPage={returnsList.current_page} totalPages={returnsList.tot_page} setCurrentPage={setCurrentPage} />
                                            <PerPageBlock perPage={perPage} setPerPage={setPerPage} />
                                        </>
                                    ) : ( !viewDataLoading && <p className="text-center w-100"></p> )}
                                    {(viewDataLoading) && <div className="loader_box ms-auto"><div className="loader_14"></div></div>}
                                </div>

                                <div className="tabs-content active" id="tab-1">
                                    <div className="row">
                                        {!viewDataLoading && returnsList.data.map((sr) => (
                                            <div className="col-md-6 col-xl-4 project-card" key={sr.id}>
                                                <div className="card hover-effect">
                                                    <div className="card-header">
                                                        <div className="d-flex align-items-center">
                                                            <div className="h-40 w-40 d-flex-center b-r-50 overflow-hidden me-2 btn-light-reddit">
                                                                 <i className="ti ti-receipt-refund f-s-18"></i>
                                                            </div>
                                                            <div className="flex-grow-1 ps-2">
                                                                <h6 className="m-0 text-dark f-w-600">{sr.party?.name || 'N/A'}</h6>
                                                                <small className="text-muted d-block">
                                                                    Return #: {sr.sales_return_number || '-'} | {formatDate(sr.return_date)}
                                                                </small>
                                                            </div>
                                                            <div className="dropdown">
                                                                <button aria-expanded="false" className="bg-none border-0" data-bs-toggle="dropdown" type="button"><i className="ti ti-dots-vertical text-dark" /></button>
                                                                <ul className="dropdown-menu dropdown-menu-end">
                                                                    <li><a className="dropdown-item" onClick={() => handleEditClick(sr.id)}><i className="ti ti-eye text-info me-1" /> View / Edit</a></li>
                                                                    <li><button className="dropdown-item delete-button" onClick={() => handleDeleteClick(sr.id)}><i className="ti ti-trash text-danger me-1" /> Delete</button></li>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="card-body py-2">
                                                         <div className="d-flex justify-content-between align-items-center">
                                                             <div>
                                                                  <p className="f-w-500 text-secondary mb-0">Credit Amount</p>
                                                                  <h6 className="f-w-600 mb-0">₹{parseFloat(sr.total_amount || 0).toFixed(2)}</h6>
                                                             </div>
                                                             {sr.sales_invoice && (
                                                                 <div className='text-end'>
                                                                      <p className="f-w-500 text-secondary mb-0">Against Invoice</p>
                                                                      <span className="badge bg-light text-dark border">{sr.sales_invoice.invoice_number}</span>
                                                                 </div>
                                                             )}
                                                         </div>
                                                     </div>
                                                </div>
                                            </div>
                                        ))}
                                        {!viewDataLoading && returnsList.data.length === 0 && (
                                            <div className="col-12"> <div className="card mb-3"><div className="row g-0"><div className="col-md-12"><div className="card-body"><h5 className="card-title">No Sales Returns Found</h5><p className="card-text">No records created yet.</p></div></div></div></div></div>
                                        )}
                                    </div>
                                </div>

                              
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default SalesReturnsPage;